import React, { useEffect, useRef, useState } from 'react';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import toast from 'react-hot-toast';
import Waveloading from '../../pages/loading/Waveloading';

const PRIMARY = "#A72C68";
const PRIMARY_LIGHT = "#FAEFF5";
const PRIMARY_BORDER = "#F0CDDB";

function LinkModal({ open, onClose, linkUrl }) {
  const listenersAdded = useRef(false);
  const [iframeLoading, setIframeLoading] = useState(true);
  // Fallback timer: if the embedded form never signals FORM_READY (e.g. an error
  // or a non-chart URL), hide the loader after a grace period so it can't hang.
  const fallbackTimer = useRef(null);

  const clearFallback = () => {
    if (fallbackTimer.current) {
      clearTimeout(fallbackTimer.current);
      fallbackTimer.current = null;
    }
  };

  useEffect(() => {
    const handleMessage = (event) => {
      if (!event?.data) return;
      switch (event.data.type) {
        case 'closeModal':
          onClose();
          break;
        case 'FORM_READY':
          // The embedded form finished loading — hide the single (centered) loader.
          clearFallback();
          setIframeLoading(false);
          break;
        case 'SHOW_TOAST':
          toast.success(event.data.message, { id: 'unique-toast' });
          break;
        case 'SHOW_TOAST_false':
          toast.error(event.data.message, { id: 'unique-toast-error' });
          break;
        default:
          break;
      }
    };

    if (!listenersAdded.current) {
      window.addEventListener('message', handleMessage);
      listenersAdded.current = true;
    }

    return () => {
      if (listenersAdded.current) {
        window.removeEventListener('message', handleMessage);
        listenersAdded.current = false;
        toast.dismiss();
      }
      clearFallback();
    };
  }, [onClose]);

  // Reset loading state whenever the drawer opens or the URL changes
  useEffect(() => {
    if (open && linkUrl) {
      setIframeLoading(true);
      clearFallback();
    }
    return clearFallback;
  }, [open, linkUrl]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: '90vw', md: '80vw', lg: '70vw', xl: '60vw' },
          maxWidth: '100vw',
          background: '#fff',
          boxShadow: '-12px 0 40px rgba(0,0,0,0.10)',
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 2.5,
            py: 2,
            borderBottom: `1px solid ${PRIMARY_BORDER}`,
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '10px',
                background: PRIMARY_LIGHT,
                border: `1px solid ${PRIMARY_BORDER}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <DescriptionOutlinedIcon sx={{ color: PRIMARY, fontSize: 20 }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#1a1a2e', lineHeight: 1.2 }}>
                Form Preview
              </Typography>
              <Typography sx={{ fontSize: '11px', color: '#6B7280', mt: 0.25 }}>
                Review or fill out the form below.
              </Typography>
            </Box>
          </Box>
          <IconButton
            size="small"
            onClick={onClose}
            sx={{
              color: '#6B7280',
              borderRadius: '8px',
              width: 32,
              height: 32,
              '&:hover': { background: PRIMARY_LIGHT, color: PRIMARY },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, background: '#F9FAFB', position: 'relative' }}>
          {iframeLoading && linkUrl && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.5,
                background: '#F9FAFB',
                zIndex: 2,
              }}
            >
              <Waveloading size={80} />
            </Box>
          )}

          {linkUrl && (
            <iframe
              key={linkUrl}
              src={linkUrl}
              title="Form Preview"
              width="100%"
              height="100%"
              style={{
                border: 'none',
                display: 'block',
                minHeight: '100%',
                opacity: iframeLoading ? 0 : 1,
                transition: 'opacity 0.2s ease',
              }}
              frameBorder="0"
              onLoad={() => {
                // Don't hide the loader here — the iframe's document has loaded but
                // the form still fetches its data. We wait for the form's FORM_READY
                // message (above) so only one loader shows. This is just a safety net
                // in case that message never arrives.
                clearFallback();
                fallbackTimer.current = setTimeout(() => setIframeLoading(false), 12000);
              }}
            />
          )}
        </Box>
      </Box>
    </Drawer>
  );
}

export default LinkModal;
